// File: src/components/forms/EmailField/EmailField.tsx
// Purpose: Form input component

import React, { useState, useEffect } from "react";
import { Mail } from "lucide-react";
import { TextField, TextFieldProps } from "../TextField";

/** 📌 Validates an email address */
const validateEmail = (email: string): string | null => {
  if (!email) return null; // ✅ No error when empty
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // 📧 Standard email format
  return emailRegex.test(email) ? null : "Invalid email format";
};

export const EmailField: React.FC<Omit<TextFieldProps, "type">> = (props) => {
  const [email, setEmail] = useState<string>(props.value || ""); // ✅ Initialize with props.value
  const [error, setError] = useState<string | null>(props.error || null);
  const [touched, setTouched] = useState(false);

  // ✅ Ensure state sync with external changes (e.g., form reset)
  useEffect(() => {
    setEmail(props.value || "");
    setError(props.error || null);
  }, [props.value, props.error]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const rawValue = e.target.value.trim();
    setEmail(rawValue);
  
    if (touched) {
      setError(validateEmail(rawValue));
    }
  
    props.onChange?.(e);
  };
  
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTouched(true);
    setError(validateEmail(email));
  
    props.onBlur?.(e);
  };

  return (
    <TextField
      {...props}
      type="email"
      icon={Mail}
      placeholder="name@example.com"
      autoComplete="on"
      value={email}
      onChange={handleChange}
      onBlur={handleBlur}
      multiline={false} // 🚫 Prevent unintended multiline use
      error={touched && error ? error : undefined} // ✅ Only show error after blur
    />
  );
};